<template>
  <section :wxid="$options.name" class="oee-graph-container">
    <figure>
      <wx-chart-line-graph
        css-classes="canvas-container"
        :styles="{
          left: graphOffset,
          width: graphWidth,
        }"
        :chart-data="getSpeedGraphData()"
        :chart-options="oeeChartOptions"
      />
    </figure>
  </section>
</template>

<script>
import StyleValues from "@/styles/_values.scss";
import WxChartLineGraph from "@/components/ui/WxChartLineGraph.vue";
import { mapGetters } from "vuex";
import i18n from "@/i18n";
import * as UnitUtils from "@/UnitUtils";
import {
  convertKilogramTo,
  convertLiterTo,
  convertMeterTo,
  COUNT_UNIT,
  LENGTH_UNIT,
  VOLUME_UNIT,
  WEIGHT_UNIT,
} from "@/UnitUtils";
import helpers, { dash } from "@/helpers";
import * as TimeUtils from "@/store/TimeUtils";
import Graphs from "@/components/Graphs";

export default {
  name: "TimelineSpeedGraph",
  components: {
    WxChartLineGraph,
  },
  data() {
    return {
      oeeChartMaxValue: 125,
    };
  },
  computed: {
    ...mapGetters("dashboard", [
      "speedGraph",
      "activeDashboardPreferencesGraph",
      "currentProductProductionObjective",
      "productionUnitConvertedUnit",
    ]),
    ...mapGetters("navigation", ["activeFactory", "isLiveData"]),
    ...mapGetters("user", ["theme"]),
    oeeChartOptions() {
      let graphKpiConfig = this.activeDashboardPreferencesGraph.config;
      const unitName = UnitUtils.getUnitLabel(this.getProductUnit(graphKpiConfig));
      let timeUnit = "";
      const graphDef = this.getGraphDefinition();
      if (graphDef === Graphs.speedPerHour) {
        timeUnit = `/${this.$t("common.h")}`;
      } else if (graphDef === Graphs.speedPerMinute) {
        timeUnit = `/${this.$t("common.m")}`;
      } else if (graphDef === Graphs.speedPerSecond) {
        timeUnit = `/${this.$t("common.s")}`;
      }
      const unitNamePerTimeUnit = unitName && unitName !== dash ? unitName + timeUnit : dash;
      return {
        isProductionObjectiveWarning: this.currentProductProductionObjective(null) === 1,
        animation: {
          duration: 0,
        },
        title: {
          display: false,
        },
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: {
            grid: {
              display: false,
            },
            ticks: {
              color: this.theme === "light" ? "black" : "white",
            },
          },
          y: {
            display: false,
            min: -1,
            ticks: {
              min: -1,
              color: this.theme === "light" ? "black" : "white",
            },
            grid: {
              display: false,
            },
          },
        },
        plugins: {
          annotation: {
            annotations: [],
          },
          legend: {
            display: false,
          },
          tooltip: {
            /**
             * DESIGN NOTE
             * Tooltips are displayed in inverted theme colors.
             * This is why the following logic on `this.theme === "light"`
             * interpolations are reversed.
             */
            enabled: true,
            displayColors: false,
            backgroundColor:
              this.theme === "light"
                ? StyleValues.color_baseBackground_themeDark
                : StyleValues.color_baseBackground_themeLight,
            bodyFont: { size: 14, weight: "bold" },
            titleFont: "{weight: 'normal'}",
            titleColor: this.theme === "light" ? StyleValues.color_text_themeDark : StyleValues.color_text_themeLight,
            footerFont: "{weight: 'normal'}",
            footerColor: this.theme === "light" ? StyleValues.color_text_themeDark : StyleValues.color_text_themeLight,
            filter: function(tooltipItem) {
              return tooltipItem.datasetIndex === 0;
            },
            callbacks: {
              title: function title(tooltipItems) {
                let tooltipItem = tooltipItems != null && tooltipItems instanceof Array ? tooltipItems[0] : null;
                let time = "";
                if (tooltipItem) {
                  let dataset = tooltipItem.dataset;
                  if (dataset) {
                    const point = dataset.data[tooltipItem.dataIndex];
                    if (point) {
                      const start = TimeUtils.toTimeHHMM(point.start_time);
                      const end = TimeUtils.toTimeHHMM(point.end_time);
                      time = `${start} ${dash} ${end}`;
                    }
                  }
                }
                return time;
              },
              label: function label(tooltipItem) {
                let pointValue = tooltipItem.dataset.data[tooltipItem.dataIndex];
                return i18n.t("graphs.speed.tooltip.speedValue") + ": " + pointValue.y + " " + unitNamePerTimeUnit;
              },
              labelTextColor: function oeePointColor(tooltipItem) {
                if (tooltipItem) {
                  let dataset = tooltipItem.dataset;
                  if (dataset) {
                    const point = dataset.data[tooltipItem.dataIndex];
                    if (point) {
                      let value = point.y ? point.y : 0.0;
                      let target = point.target ? point.target : 0.0;
                      if (value >= target) {
                        return this.theme === "light" ? StyleValues.successLight : StyleValues.successDark;
                      } else {
                        return this.theme === "light" ? StyleValues.errorLight : StyleValues.errorDark;
                      }
                    }
                  }
                }
                // Default
                return this.theme === "light" ? StyleValues.color_text_themeDark : StyleValues.color_text_themeLight;
              },
              footer: function pointFooter(tooltipItems) {
                let tooltipItem = tooltipItems != null && tooltipItems instanceof Array ? tooltipItems[0] : null;
                if (tooltipItem) {
                  let dataset = tooltipItem.dataset;
                  if (dataset) {
                    const point = dataset.data[tooltipItem.dataIndex];
                    if (point) {
                      let target = point.target !== null ? point.target : "-";
                      return i18n.t("graphs.speed.tooltip.targetValue") + ": " + target + " " + unitNamePerTimeUnit;
                    }
                  }
                }
                return "";
              },
            },
          },
        },
      };
    },
    graphWidth() {
      const intervalAmount = this.speedGraph.intervals.length - 1;
      let interval = this.speedGraph.intervalDurationInSeconds;
      let coverage = this.speedGraph.coverageDurationInSeconds;
      return ((intervalAmount * interval) / coverage) * 100 + "%";
    },
    graphOffset() {
      return (this.speedGraph.offset / this.speedGraph.coverageDurationInSeconds) * 100 + "%";
    },
  },
  methods: {
    getProductUnit(graphKpiConfig) {
      if (graphKpiConfig.product_unit) {
        return graphKpiConfig.product_unit;
      } else {
        return this.productionUnitConvertedUnit;
      }
    },
    getGraphDefinition() {
      let graphKpi = this.activeDashboardPreferencesGraph.selected_kpi;
      switch (graphKpi) {
        case Graphs.speedPerHour.name:
          return Graphs.speedPerHour;
        case Graphs.speedPerMinute.name:
          return Graphs.speedPerMinute;
        case Graphs.speedPerSecond.name:
          return Graphs.speedPerSecond;
        default:
          return null;
      }
    },
    convertForTimeUnit(valuePerHour, graphDef) {
      if (valuePerHour) {
        if (graphDef === Graphs.speedPerMinute) {
          let valuePerMinute = (1.0 * valuePerHour) / 60.0;
          return valuePerMinute >= 0.01 ? helpers.round(valuePerMinute, 2) : helpers.round(valuePerMinute, 4);
        } else if (graphDef === Graphs.speedPerSecond) {
          let valuePerSecond = (1.0 * valuePerHour) / 3600.0;
          return valuePerSecond >= 0.01 ? helpers.round(valuePerSecond, 2) : helpers.round(valuePerSecond, 4);
        }
        return helpers.round(valuePerHour, 1);
      }
      return valuePerHour;
    },
    buildPoints(unit) {
      let graphKpiConfig = this.activeDashboardPreferencesGraph.config;
      const productUnit = this.getProductUnit(graphKpiConfig);
      const unitType = UnitUtils.unitMap[productUnit].type;

      const graphDef = this.getGraphDefinition();
      return this.speedGraph.intervals.map((speedAndTargets) => {
        if (speedAndTargets) {
          if (unitType === COUNT_UNIT) {
            const count = speedAndTargets.speed_by_units[unit];
            return {
              x: speedAndTargets.index,
              y: count ? this.convertForTimeUnit(count.value, graphDef) : null,
              target: count ? this.convertForTimeUnit(count.target, graphDef) : null,
              start_time: speedAndTargets.start_time,
              end_time: speedAndTargets.end_time,
            };
          } else if (unitType === WEIGHT_UNIT) {
            let w = speedAndTargets.speed_by_units["kg"];
            w = w ? w : speedAndTargets.speed_by_units["kilogram"];
            let value = w && w.value !== null ? convertKilogramTo(w.value, unit) : null;
            let target = w && w.target ? convertKilogramTo(w.target, unit) : null;
            return {
              x: speedAndTargets.index,
              y: this.convertForTimeUnit(value, graphDef),
              target: this.convertForTimeUnit(target, graphDef),
              start_time: speedAndTargets.start_time,
              end_time: speedAndTargets.end_time,
            };
          } else if (unitType === LENGTH_UNIT) {
            const l = speedAndTargets.speed_by_units["m"];
            let value = l && l.value !== null ? convertMeterTo(l.value, unit) : null;
            let target = l && l.target !== null ? convertMeterTo(l.target, unit) : null;
            return {
              x: speedAndTargets.index,
              y: this.convertForTimeUnit(value, graphDef),
              target: this.convertForTimeUnit(target, graphDef),
              start_time: speedAndTargets.start_time,
              end_time: speedAndTargets.end_time,
            };
          } else if (unitType === VOLUME_UNIT) {
            const v = speedAndTargets.speed_by_units["L"];
            let value = v && v.value !== null ? convertLiterTo(v.value, unit) : null;
            let target = v && v.target ? convertLiterTo(v.target, unit) : null;
            return {
              x: speedAndTargets.index,
              y: this.convertForTimeUnit(value, graphDef),
              target: this.convertForTimeUnit(target, graphDef),
              start_time: speedAndTargets.start_time,
              end_time: speedAndTargets.end_time,
            };
          }
        }
        return {
          x: speedAndTargets.index,
          y: null,
          target: null,
        };
      });
    },
    getSpeedGraphData() {
      if (this.activeFactory && this.speedGraph && this.speedGraph.intervals && this.speedGraph.intervals.length > 0) {
        const graphLineColor =
          this.theme === "light" ? StyleValues.color_text_themeLight : StyleValues.color_text_themeDark;
        const targetLineColor = this.theme === "light" ? StyleValues.successLight : StyleValues.successDark;
        const pointHoverBackgroundColor =
          this.theme === "light" ? StyleValues.color_errorLight : StyleValues.color_errorDark;

        let graphKpiConfig = this.activeDashboardPreferencesGraph.config;
        const selectedUnit = this.getProductUnit(graphKpiConfig);
        const points = this.buildPoints(selectedUnit);
        const targetPoints = points.map((point) => ({
          x: point.x,
          y: point.target,
        }));
        return {
          labels: this.speedGraph.labels,
          datasets: [
            {
              order: 0,
              pointRadius: 0,
              pointHitRadius: 10,
              pointHoverBackgroundColor: pointHoverBackgroundColor,
              borderColor: graphLineColor,
              borderWidth: 2,
              backgroundColor: "transparent",
              tension: 0,
              data: points,
            },
            {
              order: 1,
              pointRadius: 0,
              pointHitRadius: 10,
              pointHoverBackgroundColor: pointHoverBackgroundColor,
              borderColor: targetLineColor,
              borderWidth: 2,
              borderDash: [3, 3],
              backgroundColor: "transparent",
              tension: 0,
              spanGaps: true,
              data: targetPoints,
            },
          ],
        };
      } else {
        return {
          labels: [],
          datasets: [],
        };
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.oee-graph-container {
  $containerDefaultHeight: 40px;

  flex: 1 0 auto;
  position: relative;
  overflow: hidden;
  height: ($containerDefaultHeight * 2);
  margin-top: 10px;
  background-color: var(--color-control-background-theme);

  @media ($wx-isNotMobile) {
    height: $containerDefaultHeight;
  }

  .canvas-container {
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    z-index: 1;

    ::v-deep canvas {
      height: 100%;
      width: 100%;
    }
  }
  .x-axe-target-line {
    position: absolute;
    width: 100%;
    left: 0;
    right: 0;
    padding-right: 4px;
    text-align: right;
    color: var(--color-error);
    border-top: 2px dashed currentColor;
  }
}
</style>
